import type { ReactElement } from 'react';
import React from 'react';

import type { PopoverAlign, PopoverCloseReason, PopoverOffset, PopoverSide } from '~/components/ui/atoms/Popover';
import Popover from '~/components/ui/atoms/Popover';

export interface StablePopoverProps {
  children: React.ReactNode;
  open: boolean;
  anchorEl: Element | null | undefined;
  onClose?: () => void;
  onOpenComplete?: () => void;
  /** Lets Escape and a press outside close the popover. Without it, only the card's own close button does. */
  closeOnBackdropClick?: boolean;
  /** Blocks every way of closing, as the card's close button is disabled meanwhile. */
  preventClose?: boolean;
  side?: PopoverSide;
  align?: PopoverAlign;
  sideOffset?: PopoverOffset;
  alignOffset?: PopoverOffset;
  /** The popup's id, which the trigger's `aria-controls` names. */
  id?: string;
  'aria-label': string;
}

/**
 * The popover behind CardDialog's `isPopover`. A card popover usually holds a form, so by default
 * neither Escape nor a press outside closes it, and an edit is not lost by accident: the card's
 * close button, and its own actions, close it. The visually hidden close button that screen
 * readers reach closes it too, since a touch screen reader has no Escape key.
 */
export default function StablePopover({
  children,
  onClose,
  closeOnBackdropClick,
  preventClose,
  ...popoverProps
}: StablePopoverProps): ReactElement {
  const handleClose = (reason: PopoverCloseReason) => {
    if (preventClose) {
      return;
    }
    if (closeOnBackdropClick || reason === 'close-press') {
      onClose?.();
    }
  };

  return (
    <Popover {...popoverProps} onClose={handleClose}>
      {children}
    </Popover>
  );
}
